import { useStore } from '@nanostores/react';
import { useSearchParams } from '@remix-run/react';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { OrderSummary } from './OrderSummary';
import { Dialog, DialogHeader } from '~/components/ui/Dialog';
import { MembersTable } from '~/components/ui/MembersTable/MembersTable';
import { getSortedOrgMembers } from '~/lib/.client/organizations';
import { teamStore } from '~/lib/stores/team';
import { userStore } from '~/lib/stores/user';

export const InviteMembersModal = () => {
  const user = useStore(userStore.user);
  const createTeamData = useStore(teamStore.createTeamData);
  const organization = createTeamData?.organization;
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!user?.featureFlags.teamManagement) {
      return;
    }

    if (searchParams.get('showMembersModal') && searchParams.get('provider')) {
      const team = user?.organizations?.find((org) => org.slug === searchParams.get('slug'));

      if (team) {
        teamStore.openTeamInviteModal(team, searchParams);
      }
    }
  }, [searchParams.get('showMembersModal')]);

  useEffect(() => {
    fetchMembers();
  }, [organization]);

  useEffect(() => {
    if (createTeamData?.members) {
      teamStore.members.set(createTeamData?.members);
    }
  }, [createTeamData?.members]);

  const fetchMembers = async () => {
    if (organization) {
      if (createTeamData?.members.length === 0) {
        teamStore.isLoadingTeam.set(true);
      }

      try {
        const sortedMembers = await getSortedOrgMembers(organization);

        const oldMembers = new Map(createTeamData.members.map((member) => [member.user.username, member] as const));

        const mergedMembers = sortedMembers.map((member) => {
          const existing = oldMembers.get(member.user.username);

          if (existing) {
            return {
              // overwrite only username + email if they have been changed
              ...existing,
              user: {
                ...existing.user,
                username: member.user.username,
                email: member.user.email,
              },
            };
          } else {
            return {
              ...member,

              // activate a subscription for newly invited team members
              withMembership: true,
            };
          }
        });

        // add creator to paid members only when it's shown for the first time
        if (oldMembers.size === 0 && mergedMembers.length && mergedMembers[0].role === 'admin') {
          mergedMembers[0].withMembership = true;
        }

        teamStore.createTeamData.set({
          ...createTeamData,
          members: mergedMembers,
        });
      } catch {
        toast.error('Failed to fetch organization members');
      } finally {
        if (createTeamData?.members.length === 0) {
          teamStore.isLoadingTeam.set(false);
        }
      }
    }
  };

  const onClose = () => {
    teamStore.createTeamData.set(null);
    teamStore.selectedMembers.set([]);

    if (searchParams.get('showMembersModal')) {
      setSearchParams([]);
    }
  };

  if (!user?.featureFlags.teamManagement) {
    return null;
  }

  return (
    <Dialog
      open={!!organization}
      onClose={onClose}
      maxWidth={1400}
      showOverflow
      contentClassName="overflow-auto"
      closeOnEscape={false}
    >
      <DialogHeader
        className="text-xl font-bold text-bolt-ds-textPrimary"
        title={<>Manage {organization?.name} Team Members</>}
      />
      <div className="flex flex-wrap lg:flex-nowrap">
        <div className="flex flex-col gap-4 py-6 px-8">
          {organization && (
            <MembersTable
              refetchMembers={fetchMembers}
              organization={organization}
              type="admin-view"
              isOnboarding={true}
            />
          )}
        </div>
        <OrderSummary />
      </div>
    </Dialog>
  );
};
